import React from 'react'

import {
  Body1,
  Body2,
  Box,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading3,
  Modal,
  ModalClose,
  ModalFooter,
  ModalHeader,
  RadioGroup,
  useModal,
} from '@gousto-internal/citrus-react'

import type { AddressOptionWithDays } from '@library/api-customer-engagement-experience'

import { ModalConfirmButton } from './ConfirmButtons'
import { useStagedSelection } from './useStagedSelection'

export const ADDRESS_MODAL_NAME = 'RESUBSCRIBE-ADDRESS-MODAL'

const AddressOptionLabel = ({ option }: { option: AddressOptionWithDays }) => (
  <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={0.5}>
    {option.name && (
      <Body1 fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
        {option.name}
      </Body1>
    )}
    <Body2 color={Color.ColdGrey_600}>{option.line1}</Body2>
    {option.town && <Body2 color={Color.ColdGrey_600}>{option.town}</Body2>}
    <Body2 color={Color.ColdGrey_600}>{option.postcode}</Body2>
  </Box>
)

type AddressModalProps = {
  options: AddressOptionWithDays[]
  selectedAddressId: string
  title: string
  subtitle: string
  onSelect: (address: AddressOptionWithDays) => void
}

export const AddressModal = ({
  options,
  selectedAddressId,
  title,
  subtitle,
  onSelect,
}: AddressModalProps) => {
  const { closeCurrentModal } = useModal()
  const [stagedAddressId, setStagedAddressId] = useStagedSelection(
    ADDRESS_MODAL_NAME,
    selectedAddressId,
  )

  const canSave = stagedAddressId !== selectedAddressId

  const handleConfirm = () => {
    const stagedAddress = options.find((option) => option.addressId === stagedAddressId)

    if (stagedAddress) {
      onSelect(stagedAddress)
    }
    closeCurrentModal()
  }

  return (
    <Modal name={ADDRESS_MODAL_NAME} onRequestClose={closeCurrentModal}>
      <ModalHeader>
        <Heading3>{title}</Heading3>
        <ModalClose onClose={closeCurrentModal} />
      </ModalHeader>
      <Box paddingH={[4, 6]} paddingV={[4, 6]} paddingBottom={[0, 0]}>
        <Box paddingBottom={4}>
          <Body2 color={Color.ColdGrey_600}>{subtitle}</Body2>
        </Box>
        <Box
          maxHeight="40vh"
          style={{ overflowY: 'auto' }}
          paddingBottom={4}
          data-testid="address-modal-scroll"
        >
          <RadioGroup
            outline
            value={stagedAddressId}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setStagedAddressId(event.target.value)
            }
            options={options.map((option) => ({
              name: 'address',
              value: option.addressId,
              label: <AddressOptionLabel option={option} />,
            }))}
          />
        </Box>
      </Box>
      <ModalFooter>
        <ModalConfirmButton
          colorVariant={ButtonColorVariant.Primary}
          width="100%"
          onClick={handleConfirm}
          disabled={!canSave}
        >
          Confirm delivery address
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  )
}
